import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, Chip, LinearProgress, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Tabs, Tab,
} from '@mui/material';
import { ArrowBack as BackIcon, CheckCircle as CheckIcon, Cancel as MissingIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import {
    useInventuraQuery,
    usePolozkyInventuryQuery,
    useDokoncitInventuruMutation,
    useSkenPolozkyMutation,
    useOznacNalezenaMutation,
} from '../../api/JsdhNotifikaceService/service/InventarMutations';
import dayjs from 'dayjs';

const formatDate = (d?: string) => d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '—';

const InventuraDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const inventuraId = parseInt(id ?? '0');
    const [tab, setTab] = React.useState(0);
    const [skenKod, setSkenKod] = React.useState('');
    const skenInputRef = React.useRef<HTMLInputElement>(null);

    const { data: inventura, isLoading, refetch: refetchInventura } = useInventuraQuery(inventuraId);
    const probihajici = inventura?.stav === 'PROBIHAJICI';
    const { data: polozky, refetch: refetchPolozky } = usePolozkyInventuryQuery(inventuraId, {
        refetchInterval: probihajici ? 10000 : false,
    });
    const dokoncitMutation = useDokoncitInventuruMutation();
    const skenMutation = useSkenPolozkyMutation();
    const oznacNalezenaMutation = useOznacNalezenaMutation();

    const handleObnovit = () => {
        refetchInventura();
        refetchPolozky();
    };

    const handleSkenKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const kod = skenKod.trim();
        if (!kod) return;
        // Pole se záměrně čistí až v onSuccess — při chybě zůstává hodnota v poli, aby šla opravit a znovu odeslat.
        // Trade-off: velmi rychlý druhý sken provedený během probíhajícího (dosud neúspěšného) requestu
        // se může slít se zbytkem neodeslané hodnoty v poli — akceptováno, USB čtečka + reálné použití toto riziko minimalizují.
        skenMutation.mutate({ inventuraId, data: { kod, typKodu: 'QR' } }, {
            onSuccess: () => {
                setSkenKod('');
                skenInputRef.current?.focus();
            },
            onError: () => {
                skenInputRef.current?.focus();
            },
        });
    };

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
    if (!inventura) return <Typography>Inventura nenalezena</Typography>;

    const progress = inventura.celkemPolozek > 0 ? (inventura.nalezenoPolozek / inventura.celkemPolozek) * 100 : 0;
    const nalezene = polozky?.filter((p) => p.nalezena) ?? [];
    const chybejici = polozky?.filter((p) => !p.nalezena) ?? [];
    const colSpan = 5 + (tab === 0 ? 2 : 0) + (tab === 1 && probihajici ? 1 : 0);

    return (
        <Box>
            <PageHeader
                title={inventura.nazev}
                icon={<BackIcon sx={{ cursor: 'pointer' }} onClick={() => navigate('/admin/inventura')} />}
                action={
                    probihajici ? (
                        <>
                            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleObnovit}>
                                Obnovit
                            </Button>
                            <Button variant="contained" color="warning" onClick={() => dokoncitMutation.mutate(inventuraId)}
                                    disabled={dokoncitMutation.isPending}>
                                Dokončit inventuru
                            </Button>
                        </>
                    ) : null
                }
            />

            {probihajici && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <TextField
                        inputRef={skenInputRef}
                        autoFocus
                        fullWidth
                        label="Sken QR kódu"
                        placeholder="Naskenujte QR kód položky (USB čtečka)"
                        value={skenKod}
                        onChange={(e) => setSkenKod(e.target.value)}
                        onKeyDown={handleSkenKeyDown}
                        InputProps={{
                            endAdornment: skenMutation.isPending ? <CircularProgress size={16} /> : undefined,
                        }}
                    />
                </Paper>
            )}

            <Paper sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Stav</Typography>
                        <Chip
                            label={inventura.stav === 'PROBIHAJICI' ? 'Probíhá' : inventura.stav === 'DOKONCENA' ? 'Dokončena' : inventura.stav}
                            color={inventura.stav === 'PROBIHAJICI' ? 'info' : 'success'}
                            size="small"
                        />
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Typ</Typography>
                        <Typography>{inventura.typ}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Zahájeno</Typography>
                        <Typography>{formatDate(inventura.datumZahajeni)}</Typography>
                    </Box>
                    {inventura.datumUkonceni && (
                        <Box>
                            <Typography variant="body2" color="text.secondary">Ukončeno</Typography>
                            <Typography>{formatDate(inventura.datumUkonceni)}</Typography>
                        </Box>
                    )}
                    <Box>
                        <Typography variant="body2" color="text.secondary">Vytvořil</Typography>
                        <Typography>{inventura.vytvorilJmeno ?? '—'}</Typography>
                    </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Nalezeno: {inventura.nalezenoPolozek} / {inventura.celkemPolozek}</Typography>
                        <Typography variant="body2">{Math.round(progress)} %</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
                    {inventura.chybejiciPolozek > 0 && (
                        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                            Chybí: {inventura.chybejiciPolozek} položek
                        </Typography>
                    )}
                </Box>
            </Paper>

            <Paper sx={{ mb: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label={`Nalezené (${nalezene.length})`} />
                    <Tab label={`Chybějící (${chybejici.length})`} />
                </Tabs>
            </Paper>

            <Paper sx={{ p: 2 }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell>Položka</TableCell>
                                <TableCell>Kategorie</TableCell>
                                <TableCell>QR kód</TableCell>
                                <TableCell>INV číslo</TableCell>
                                {tab === 0 && <TableCell>Stav</TableCell>}
                                {tab === 0 && <TableCell>Skenoval</TableCell>}
                                {tab === 1 && probihajici && <TableCell></TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(tab === 0 ? nalezene : chybejici).map((p) => (
                                <TableRow key={p.id}>
                                    <TableCell>
                                        {p.nalezena
                                            ? <CheckIcon color="success" fontSize="small" />
                                            : <MissingIcon color="error" fontSize="small" />}
                                    </TableCell>
                                    <TableCell>{p.polozkaNazev}</TableCell>
                                    <TableCell>{p.kategorieNazev}</TableCell>
                                    <TableCell><Typography variant="body2" fontFamily="monospace">{p.polozkaQrKod}</Typography></TableCell>
                                    <TableCell>{p.polozkaInventarniCislo ?? '—'}</TableCell>
                                    {tab === 0 && <TableCell><Chip label={p.stavPriNalezeni ?? '—'} size="small" variant="outlined" /></TableCell>}
                                    {tab === 0 && <TableCell>{p.skenovanoKymJmeno ?? '—'}</TableCell>}
                                    {tab === 1 && probihajici && (
                                        <TableCell>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={oznacNalezenaMutation.isPending}
                                                onClick={() => oznacNalezenaMutation.mutate({ inventuraId, polozkaId: p.polozkaId })}
                                            >
                                                Nalezena
                                            </Button>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {(tab === 0 ? nalezene : chybejici).length === 0 && (
                                <TableRow><TableCell colSpan={colSpan} align="center">Žádné položky</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default InventuraDetailPage;
